import {
  paymentModels,
  pensionDeductionMethods,
} from '@nxsys/constants/AppConstants';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import {
  getShownEmployeePension,
  getShownEmployersPension,
  getShownPensionValue,
} from '@nxsys/utils/reported-pension';
import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { includes, isEmpty } from 'lodash';
import moment from 'moment-timezone';

const getPayslipStatus = (record: any): { label: string; color: string } => {
  if (
    record?.payrollRef?.isConfirmedPaymentMade &&
    record?.payrollRef?.paymentDate
  ) {
    return { label: 'Paid', color: 'success' };
  }
  return { label: 'Upcoming', color: 'processing' };
};

export type GetPayslipColumnsParams = {
  payrollCountry: PayrollCountryType;
  permissions: string[];
  paymentModel?: string;
  onDownload: (id: string) => void;
  onViewDetail: (id: string) => void;
  messages: TypedMessages;
  downloadLoadingId?: string | null;
};

const PERMISSION_HOLIDAY_PAY = 'EMPLOYEE.ME.HOLIDAY_PAY_DETAILS.SHOW';

/**
 * Ireland payrolls leave `payeeUmbrellaValue` empty and don't populate the
 * `totalDeduction` roll-up, so fall back to summing the statutory deductions
 * held on `irelandValue`.
 */
export function getIrelandTotalDeduction(payrollRef: any): number {
  if (payrollRef?.totalDeduction) return payrollRef.totalDeduction;
  if (payrollRef?._totalDeduction) return payrollRef._totalDeduction;
  const irl = payrollRef?.irelandValue;
  return (
    (irl?.tax || 0) +
    (irl?.usc || 0) +
    (irl?.eePRSI || 0) +
    (irl?.eePension || 0) +
    (irl?.lptDeducted || 0)
  );
}

export function getIrelandColumns({
  permissionHP,
  currencySymbol,
  onDownload,
  onViewDetail,
  messages,
  downloadLoadingId,
}: {
  permissionHP: boolean;
  currencySymbol: string;
  onDownload: (id: string) => void;
  onViewDetail: (id: string) => void;
  messages: TypedMessages;
  downloadLoadingId?: string | null;
}): ColumnsType<any> {
  const cols: ColumnsType<any> = [
    {
      key: 'actions',
      title: messages['common.action'] || 'Actions',
      align: 'left',
      width: 120,
      fixed: 'left',
      render: (_, record) => (
        <ul className="flex items-center justify-center gap-2">
          <li>
            <Tooltip title="View">
              <span
                className="cursor"
                onClick={() => onViewDetail(record?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onViewDetail(record?.id)}
              >
                <Icons.IconEye />
              </span>
            </Tooltip>
          </li>
          <li
            className={downloadLoadingId === record?.id ? 'pointer-events-none text-primary-500' : ''}
            onClick={() => onDownload(record?.id)}
          >
            <Tooltip title={downloadLoadingId === record?.id ? 'Loading...' : 'Download'}>
              <span
                className="cursor"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onDownload(record?.id)}
              >
                {downloadLoadingId === record?.id ? (
                  <LoadingOutlined style={{ fontSize: 16 }} spin />
                ) : (
                  <Icons.IconDownload />
                )}
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'id',
      title: messages['common.payslipsId'] || 'Payslips ID',
      fixed: 'left',
      dataIndex: 'id',
      width: 200,
      render: (value: string) => (value || '')?.replace('SFB', 'PAY') || '_',
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'] || 'Tax Period',
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (_, record) =>
        `${record?.taxPeriod || ''}/${record?.taxYear || record?.currentYear || ''}`,
    },
    {
      key: 'companyRef',
      title: messages['common.employer'] || 'Employer',
      dataIndex: ['companyRef', 'companyName'],
      width: 250,
      render: (value: string) => value || '_',
    },
    {
      key: 'timesheetValue',
      title: messages['common.grossPay'] || 'Gross Pay',
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'hours',
      title: messages['common.units'] || 'Units',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (pr: any) =>
        pr?.totalHours ??
        pr?._totalHours ??
        '',
    },
    {
      key: 'salary',
      title: messages['common.salary'] || 'Salary',
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'otherPayment',
      title: messages['common.otherPayment'] || 'Other Payment',
      dataIndex: 'otherPayment',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'otherDeduction',
      title: messages['common.otherDeduction'] || 'Other Deduction',
      dataIndex: 'otherDeduction',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'grossForTax',
      title: messages['common.taxableGross'] || 'Taxable Gross',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (pr: any) =>
        formatMoney(
          pr?.irelandValue?.taxGross ||
            pr?.payeeUmbrellaValue?.grossForTax ||
            0,
          currencySymbol,
        ),
    },
    {
      key: 'taxDeducted',
      title: messages['common.taxDeducted'] || 'Tax Deducted',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (pr: any) =>
        formatMoney(
          pr?.irelandValue?.tax || pr?.payeeUmbrellaValue?.tax || 0,
          currencySymbol,
        ),
    },
    {
      key: 'uscGross',
      title: 'USCable Gross',
      dataIndex: ['payrollRef', 'irelandValue', 'uscGross'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'usc',
      title: 'USC',
      dataIndex: ['payrollRef', 'irelandValue', 'usc'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'eePrsiGross',
      title: 'Employee PRSIable Gross',
      dataIndex: ['payrollRef', 'irelandValue', 'eePrsiGross'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'eePRSI',
      title: 'Employee PRSI',
      dataIndex: ['payrollRef', 'irelandValue', 'eePRSI'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'erPrsiGross',
      title: 'Employer PRSIable Gross',
      dataIndex: ['payrollRef', 'irelandValue', 'erPrsiGross'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'erPRSI',
      title: 'Employer PRSI',
      dataIndex: ['payrollRef', 'irelandValue', 'erPRSI'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'pensionGross',
      title: 'MFF Gross',
      dataIndex: ['payrollRef', 'irelandValue', 'pensionGross'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'eePension',
      title: 'Employee MFF',
      dataIndex: ['payrollRef', 'irelandValue', 'eePension'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'erPension',
      title: 'Employer MFF',
      dataIndex: ['payrollRef', 'irelandValue', 'erPension'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'lptDeducted',
      title: 'LPT',
      dataIndex: ['payrollRef', 'irelandValue', 'lptDeducted'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'netPay',
      title: messages['common.netPay'] || 'Net Pay',
      dataIndex: 'netPay',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    ...(permissionHP
      ? [
          {
            key: 'advancedHolidayPay',
            title:
              messages['common.advancedHolidayPay'] || 'Advanced Holiday Pay',
            dataIndex: ['payrollRef', 'irelandValue', 'holidayPayAdvanced'],
            align: 'right' as const,
            width: 250,
            render: (value: number) => formatMoney(value || 0, currencySymbol),
          },
          {
            key: 'holidayPayRetained',
            title:
              messages['common.holidayPayRetained'] || 'Holiday Pay Retained',
            dataIndex: ['payrollRef', 'retainedHolidayPay'],
            align: 'right' as const,
            width: 250,
            render: (value: number) => formatMoney(value || 0, currencySymbol),
          },
          {
            key: 'holidayPaid',
            title: messages['common.holidayPaid'] || 'Holiday Paid',
            dataIndex: 'payrollRef',
            align: 'right' as const,
            width: 250,
            render: (_: unknown, record: any) =>
              formatMoney(
                (record?.payrollRef?.holidayPaid || 0) -
                  (record?.payrollRef?.irelandValue?.holidayPayAdvanced || 0),
                currencySymbol,
              ),
          },
        ]
      : []),
    {
      key: 'totalDeduction',
      title: messages['common.totalDeduction'] || 'Total Deduction',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (pr: any) =>
        formatMoney(getIrelandTotalDeduction(pr), currencySymbol),
    },
    {
      key: 'ssp',
      title: messages['common.sickPaySSP'] || 'Sick Pay (SSP)',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'ssp'],
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
  ];
  return cols;
}

export function getDefaultColumns({
  permissionHP,
  currencySymbol,
  onDownload,
  onViewDetail,
  messages,
  downloadLoadingId,
}: {
  permissionHP: boolean;
  currencySymbol: string;
  onDownload: (id: string) => void;
  onViewDetail: (id: string) => void;
  messages: TypedMessages;
  downloadLoadingId?: string | null;
}): ColumnsType<any> {
  const base: (ColumnsType<any>[number] | null)[] = [
    {
      key: 'actions',
      title: messages['common.action'] || 'Actions',
      align: 'left',
      width: 120,
      fixed: 'left',
      render: (_, record) => (
        <ul className="flex items-center justify-center gap-2">
          <li>
            <Tooltip title="View">
              <span
                className="cursor"
                onClick={() => onViewDetail(record?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onViewDetail(record?.id)}
              >
                <Icons.IconEye />
              </span>
            </Tooltip>
          </li>
          <li
            className={downloadLoadingId === record?.id ? 'pointer-events-none text-primary-500' : ''}
            onClick={() => onDownload(record?.id)}
          >
            <Tooltip title={downloadLoadingId === record?.id ? 'Loading...' : 'Download'}>
              <span
                className="cursor"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onDownload(record?.id)}
              >
                {downloadLoadingId === record?.id ? (
                  <LoadingOutlined style={{ fontSize: 16 }} spin />
                ) : (
                  <Icons.IconDownload />
                )}
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'id',
      title: messages['common.payslipsId'] || 'Payslips ID',
      dataIndex: 'id',
      fixed: 'left',
      width: 200,
      render: (value: string) => (value || '')?.replace('SFB', 'PAY') || '_',
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'] || 'Tax Period',
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (_, record) =>
        `${record?.taxPeriod || ''}/${record?.taxYear || record?.currentYear || ''}`,
    },
    {
      key: 'companyRef',
      title: messages['common.employer'] || 'Employer',
      dataIndex: ['companyRef', 'companyName'],
      width: 250,
      render: (value: string) => value || '_',
    },
    {
      key: 'timesheetValue',
      title: messages['common.timesheetValue'] || 'Timesheet Value',
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'hours',
      title: messages['common.units'] || 'Units',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'totalHours'],
      align: 'right',
      width: 250,
      render: (value: string | number) => value ?? '',
    },
    {
      key: 'margin',
      title: messages['common.margin'] || 'Margin',
      dataIndex: 'margin',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'apprenticeLevy',
      title: messages['common.apprenticeLevy'] || 'Apprentice Levy',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'appLevy'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'salary',
      title: messages['common.salary'] || 'Salary',
      dataIndex: ['payrollRef', 'salary'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    permissionHP
      ? {
          key: 'advancedHolidayPay',
          title:
            messages['common.advancedHolidayPay'] || 'Advanced Holiday Pay',
          dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'holidayPay'],
          align: 'right' as const,
          width: 250,
          render: (value: number) => formatMoney(value || 0, currencySymbol),
        }
      : null,
    {
      key: 'expenseClaimed',
      title: messages['common.expensesClaimed'] || 'Expenses Claimed',
      dataIndex: ['payrollRef', 'expenseClaimed'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'expensesPaid',
      title: messages['common.expensesPaid'] || 'Expenses Paid',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'expensesPaid'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'bonus',
      title: messages['common.bonus'] || 'Bonus',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'bonus'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'otherPayment',
      title: messages['common.otherPayment'] || 'Other Payment',
      dataIndex: 'otherPayment',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'otherDeduction',
      title: messages['common.otherDeduction'] || 'Other Deduction',
      dataIndex: 'otherDeduction',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'totalPayment',
      title: messages['common.totalPayment'] || 'Total Payment',
      dataIndex: ['payrollRef', 'totalPayment'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'tax',
      title: messages['common.taxDeducted'] || 'Tax Deducted',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'tax'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'employeeNI',
      title: messages['common.employeeNI'] || 'Employee NI',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'employeeNI'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'employersNI',
      title: messages['common.employerNI'] || 'Employer NI',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'employersNI'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'employeePension',
      title: messages['common.employeePension'] || 'Employee Pension',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (value: any) =>
        formatMoney(getShownEmployeePension(value), currencySymbol),
    },
    {
      key: 'eePensionDeductionMethod',
      title:
        messages['common.employeeDeductionMethod'] ||
        'Employee Deduction Method',
      dataIndex: [
        'payrollRef',
        'payeeUmbrellaValue',
        'eePensionDeductionMethod',
      ],
      width: 250,
      render: (value: string) =>
        pensionDeductionMethods.find((item) => item.value === value)?.label ??
        'Percentage (%)',
    },
    {
      key: 'employersPension',
      title: messages['common.employerPension'] || 'Employer Pension',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (value: any) =>
        formatMoney(getShownEmployersPension(value), currencySymbol),
    },
    {
      key: 'erPensionDeductionMethod',
      title:
        messages['common.employerDeductionMethod'] ||
        'Employer Deduction Method',
      dataIndex: [
        'payrollRef',
        'payeeUmbrellaValue',
        'erPensionDeductionMethod',
      ],
      width: 200,
      render: (value: string) =>
        pensionDeductionMethods.find((item) => item.value === value)?.label ??
        'Percentage (%)',
    },
    permissionHP
      ? {
          key: 'rolledUpHolidayPay',
          title:
            messages['common.advancedHolidayPay'] || 'Advanced Holiday Pay',
          dataIndex: ['rolledUpHolidayPay'],
          align: 'right' as const,
          width: 200,
          render: (value: number) => formatMoney(value || 0, currencySymbol),
        }
      : null,
    permissionHP
      ? {
          key: 'retainedHolidayPay',
          title:
            messages['common.holidayPayRetained'] || 'Holiday Pay Retained',
          dataIndex: ['payrollRef', 'retainedHolidayPay'],
          align: 'right' as const,
          width: 250,
          render: (value: number) => formatMoney(value || 0, currencySymbol),
        }
      : null,
    permissionHP
      ? {
          key: 'holidayPaid',
          title: messages['common.holidayPaid'] || 'Holiday Paid',
          dataIndex: 'payrollRef',
          align: 'right' as const,
          width: 250,
          render: (_: unknown, record: any) =>
            formatMoney(
              (record?.payrollRef?.holidayPaid || 0) -
                (record?.payrollRef?.payeeUmbrellaValue?.holidayPay || 0),
              currencySymbol,
            ),
        }
      : null,
    {
      key: 'totalDeduction',
      title: messages['common.totalDeduction'] || 'Total Deduction',
      dataIndex: ['payrollRef', 'totalDeduction'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'netPay',
      title: messages['common.netPay'] || 'Net Pay',
      dataIndex: 'netPay',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'studentLoan',
      title: messages['common.studentLoan'] || 'Student Loan',
      dataIndex: ['payrollRef', 'studentLoan'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'postgraduateLoan',
      title: messages['common.pgStudentLoan'] || 'PG Student Loan',
      dataIndex: ['payrollRef', 'postgraduateLoan'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'attachmentOrderDeducted',
      title: messages['common.attachment'] || 'Attachment',
      dataIndex: [
        'payrollRef',
        'payeeUmbrellaValue',
        'attachmentOrderDeducted',
      ],
      align: 'right',
      width: 200,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'slickPay',
      title: messages['common.sickPaySSP'] || 'Sick Pay (SSP)',
      dataIndex: 'slickPay',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'maternityPay',
      title: messages['common.maternityPaySMP'] || 'Maternity Pay (SMP)',
      dataIndex: 'maternityPay',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'adoptionPay',
      title: messages['common.adoptionPaySAP'] || 'Adoption Pay (SAP)',
      dataIndex: 'adoptionPay',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'paternityPay',
      title: messages['common.paternityPaySPP'] || 'Paternity Pay (SPP)',
      dataIndex: 'paternityPay',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'additionalSPP',
      title: messages['common.additionalSPP'] || 'Additional SPP',
      dataIndex: 'additionalSPP',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'paymentDate',
      title: messages['common.paymentDate'] || 'Payment Date',
      dataIndex: ['payrollRef', 'paymentDate'],
      align: 'right',
      width: 250,
      render: (value: string) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'paid',
      title: messages['common.paid'] || 'Paid',
      dataIndex: ['payrollRef', 'paid'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'outstanding',
      title: messages['common.outstanding'] || 'Outstanding',
      dataIndex: ['payrollRef', 'outstanding'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'fpsAmount',
      title: messages['common.fpsAmount'] || 'FPS Amount',
      dataIndex: 'fpsAmount',
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'grossForNI',
      title: messages['common.grossForNI'] || 'Gross For NI',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'grossForTaxAndNI'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'grossForTaxYTD',
      title: messages['common.grossForTaxYTD'] || 'Gross For Tax YTD',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'taxYTD'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'grossPayYTD',
      title: messages['common.grossPayYTD'] || 'Gross for NI YTD',
      dataIndex: ['payrollRef', 'payeeUmbrellaValue', 'grossPayYTD'],
      align: 'right',
      width: 250,
      render: (value: number) => formatMoney(value || 0, currencySymbol),
    },
  ];
  return base.filter((c): c is NonNullable<typeof c> => c != null);
}

export function getColumnsCis({
  payrollCountry,
  onDownload,
  onViewDetail,
  downloadLoadingId,
}: {
  payrollCountry: PayrollCountryType;
  onDownload: (id: string) => void;
  onViewDetail: (id: string) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<any> {
  const currencySymbol = showCurrencyByCountry(payrollCountry);
  return [
    {
      key: 'actions',
      title: 'Actions',
      align: 'left',
      width: 120,
      fixed: 'left',
      render: (_, record) => (
        <ul className="flex items-center justify-center gap-2">
          <li>
            <Tooltip title="View">
              <span
                className="cursor"
                onClick={() => onViewDetail(record?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onViewDetail(record?.id)}
              >
                <Icons.IconEye />
              </span>
            </Tooltip>
          </li>
          <li
            className={downloadLoadingId === record?.id ? 'pointer-events-none text-primary-500' : ''}
            onClick={() => onDownload(record?.id)}
          >
            <Tooltip title={downloadLoadingId === record?.id ? 'Loading...' : 'Download'}>
              <span
                className="cursor"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onDownload(record?.id)}
              >
                {downloadLoadingId === record?.id ? (
                  <LoadingOutlined style={{ fontSize: 16 }} spin />
                ) : (
                  <Icons.IconDownload />
                )}
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'id',
      title: 'Self Bill Ref',
      dataIndex: 'id',
    },
    {
      key: 'invoiceRef',
      title: 'Invoice Ref',
      dataIndex: 'invoiceRef',
    },
    {
      title: 'Timesheet ID',
      // dataIndex: 'timesheetRef',
      render: (record) => (
        <>
          {!isEmpty(record.timeSheets) ? (
            <ul>
              {record.timeSheets.map((ts: any) => (
                <li key={ts.id} className="mb-1">
                  {ts.id}
                </li>
              ))}
            </ul>
          ) : (
            record.timesheetRef || '_'
          )}
        </>
      ),
    },
    {
      key: 'billDate',
      title: 'Bill Date',
      dataIndex: 'billDate',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
    },
    {
      key: 'timesheetValue',
      title: 'Timesheet Value',
      dataIndex: 'timesheetValue',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value, currencySymbol),
    },
    {
      key: 'margin',
      title: 'Margin',
      dataIndex: 'margin',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value, currencySymbol),
    },
    {
      key: 'gross',
      title: 'Gross',
      dataIndex: 'gross',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value, currencySymbol),
    },
    {
      key: 'vat',
      title: 'VAT',
      dataIndex: 'vat',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value, currencySymbol),
    },
    {
      key: 'cisStatus',
      title: 'Cis Status',
      dataIndex: 'cisStatus',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value || '_',
    },
    {
      key: 'cisDeduction',
      title: 'Cis Deduction',
      dataIndex: 'cisDeduction',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value, currencySymbol),
    },
    {
      key: 'netPay',
      title: 'Net Pay',
      dataIndex: 'netPay',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value, currencySymbol),
    },
  ];
}

export function getColumnsPayeeUmbrella({
  permissionHP,
  payrollCountry,
  onDownload,
  onViewDetail,
  downloadLoadingId,
}: {
  permissionHP: boolean;
  payrollCountry: PayrollCountryType;
  onDownload: (id: string) => void;
  onViewDetail: (id: string) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<any> {
  const currencySymbol = showCurrencyByCountry(payrollCountry);
  const base: (ColumnsType<any>[number] | null)[] = [
    {
      key: 'actions',
      title: 'Actions',
      align: 'left',
      fixed: 'left',
      width: 120,
      render: (_, record) => (
        <ul className="flex items-center justify-center gap-2">
          <li>
            <Tooltip title="View">
              <span
                className="cursor"
                onClick={() => onViewDetail(record?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onViewDetail(record?.id)}
              >
                <Icons.IconEye />
              </span>
            </Tooltip>
          </li>
          <li
            className={downloadLoadingId === record?.id ? 'pointer-events-none text-primary-500' : ''}
            onClick={() => onDownload(record?.id)}
          >
            <Tooltip title={downloadLoadingId === record?.id ? 'Loading...' : 'Download'}>
              <span
                className="cursor"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onDownload(record?.id)}
              >
                {downloadLoadingId === record?.id ? (
                  <LoadingOutlined style={{ fontSize: 16 }} spin />
                ) : (
                  <Icons.IconDownload />
                )}
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'id',
      title: 'Payslips ID',
      dataIndex: 'id',
      fixed: 'left',
      width: 200,
      render: (value) => value?.replace('SFB', 'PAY') || '_',
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        `${value?.taxPeriod || ''}/${
          value?.taxYear || value?.currentYear || ''
        }`,
    },
    {
      key: 'paymentModel',
      title: 'Payment Model',
      dataIndex: 'payrollRef',
      render: (values) =>
        values?.paymentModel
          ? paymentModels[values.paymentModel as keyof typeof paymentModels]
          : '_',
    },
    {
      key: 'paymentCompany',
      title: 'Payment Company',
      dataIndex: 'companyRef',
      width: 350,
      render: (values) => values?.companyName || '_',
    },
    {
      key: 'agencyName',
      title: 'Agency Name',
      dataIndex: 'payrollRef',
      width: 350,
      render: (values) => values.agencyName || '_',
    },
    {
      key: 'timesheetValue',
      title: 'Timesheet Value',
      dataIndex: 'timesheetValue',
      align: 'right',
      sorter: true,
      width: 200,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'hours',
      title: 'Hours',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => props?.payeeUmbrellaValue?.totalHours || '',
    },
    {
      key: 'margin',
      title: 'Margin',
      dataIndex: 'margin',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'payrollRef',
      title: 'Apprentice Levy',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.appLevy || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'salary',
      title: 'Salary',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.salary || 0, currencySymbol)}</span>
      ),
    },
    permissionHP
      ? {
          key: 'holidayPay',
          title: 'Advanced Holiday Pay',
          dataIndex: 'payrollRef',
          align: 'right',
          width: 200,
          render: (props) => (
            <span>
              {formatMoney(
                props?.payeeUmbrellaValue?.holidayPay || 0,
                currencySymbol,
              )}
            </span>
          ),
        }
      : null,
    {
      key: 'expenseClaimed',
      title: 'Expenses Claimed',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 200,
      render: (props) => (
        <span>{formatMoney(props?.expenseClaimed || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'expensesPaid',
      title: 'Expenses Paid',
      dataIndex: 'payrollRef',
      align: 'right',
      width: 200,
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.expensesPaid || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'bonus',
      title: 'Bonus',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.bonus || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'otherPayment',
      title: 'Other Payment',
      dataIndex: 'otherPayment',
      align: 'right',
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'otherDeduction',
      title: 'Other Deduction',
      dataIndex: 'otherDeduction',
      align: 'right',
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'totalPayment',
      title: 'Total Payment',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.totalPayment || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'tax',
      title: 'Tax Deducted',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.tax || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'employeeNI',
      title: 'Employee NI',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.employeeNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'employersNI',
      title: 'Employer NI',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.employersNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'employeePension',
      title: 'Employee Pension',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            getShownPensionValue(props, 'employeePension') || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'employersPension',
      title: 'Employer Pension',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            getShownPensionValue(props, 'employersPension') || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    permissionHP
      ? {
          key: 'holidayPayRetained',
          title: 'Holiday Pay Retained',
          dataIndex: 'payrollRef',
          align: 'right',
          render: (props) => (
            <span>
              {formatMoney(
                props?.payeeUmbrellaValue?.holidayPay || 0,
                currencySymbol,
              )}
            </span>
          ),
        }
      : null,
    permissionHP
      ? {
          key: 'holidayPaid',
          title: 'Holiday Paid',
          dataIndex: 'payrollRef',
          align: 'right',
          render: (props) => (
            <span>
              {formatMoney(
                (props?.holidayPaid || 0) -
                  (props?.payeeUmbrellaValue?.holidayPay || 0),
                currencySymbol,
              )}
            </span>
          ),
        }
      : null,
    {
      key: 'totalDeduction',
      title: 'Total Deduction',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.totalDeduction || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'netPay',
      title: 'Net Pay',
      dataIndex: 'netPay',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'studentLoan',
      title: 'Student Loan',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.studentLoan || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'postgraduateLoan',
      title: 'PG Student Loan',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.postgraduateLoan || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'attachment',
      title: 'Attachment',
      dataIndex: 'attachment',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'slickPay',
      title: 'Sick Pay (SSP)',
      dataIndex: 'slickPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'maternityPay',
      title: 'Maternity Pay (SMP)',
      dataIndex: 'maternityPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'adoptionPay',
      title: 'Adoption Pay (SAP)',
      dataIndex: 'adoptionPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'paternityPay',
      title: 'Paternity Pay (SPP)',
      dataIndex: 'paternityPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'additionalSPP',
      title: 'Additional SPP',
      dataIndex: 'additionalSPP',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'payrollRef',
      title: 'Payment Date',
      dataIndex: 'payrollRef',
      align: 'right',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (values) =>
        values?.paymentDate
          ? moment(values?.paymentDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'paid',
      title: 'Paid',
      align: 'right',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>{formatMoney(props?.paid || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'outstanding',
      title: 'Outstanding',
      align: 'right',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>{formatMoney(props?.outstanding || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'fpsAmount',
      title: 'FPS Amount',
      dataIndex: 'fpsAmount',
      align: 'right',
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'grossForTax',
      title: 'Gross For Tax',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.grossForTaxAndNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'grossForNI',
      title: 'Gross For NI',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.grossForTaxAndNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'grossForTaxYTD',
      title: 'Gross For Tax YTD',
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.taxYTD || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'grossPayYTD',
      title: 'Gross for NI YTD',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.grossPayYTD || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
  ];
  return base.filter((c): c is NonNullable<typeof c> => c != null);
}

export function getColumnsHybrid({
  payrollCountry,
  onDownload,
  onViewDetail,
  downloadLoadingId,
}: {
  payrollCountry: PayrollCountryType;
  onDownload: (id: string) => void;
  onViewDetail: (id: string) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<any> {
  const currencySymbol = showCurrencyByCountry(payrollCountry);
  return [
    {
      key: 'actions',
      title: 'Actions',
      align: 'left',
      fixed: 'left',
      width: 120,
      render: (_, record) => (
        <ul className="flex items-center justify-center gap-2">
          <li>
            <Tooltip title="View">
              <span
                className="cursor"
                onClick={() => onViewDetail(record?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onViewDetail(record?.id)}
              >
                <Icons.IconEye />
              </span>
            </Tooltip>
          </li>
          <li
            className={downloadLoadingId === record?.id ? 'pointer-events-none text-primary-500' : ''}
            onClick={() => onDownload(record?.id)}
          >
            <Tooltip title={downloadLoadingId === record?.id ? 'Loading...' : 'Download'}>
              <span
                className="cursor"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onDownload(record?.id)}
              >
                {downloadLoadingId === record?.id ? (
                  <LoadingOutlined style={{ fontSize: 16 }} spin />
                ) : (
                  <Icons.IconDownload />
                )}
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'id',
      title: 'Payslips ID',
      dataIndex: 'id',
      fixed: 'left',
      render: (value) => value?.replace('SFB', 'PAY') || '_',
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        `${value?.taxPeriod || ''}/${
          value?.taxYear || value?.currentYear || ''
        }`,
    },
    {
      key: 'paymentModel',
      title: 'Payment Model',
      dataIndex: 'payrollRef',
      render: (values) =>
        values?.paymentModel
          ? paymentModels[values.paymentModel as keyof typeof paymentModels]
          : '_',
    },
    {
      key: 'paymentCompany',
      title: 'Payment Company',
      dataIndex: 'companyRef',
      width: 350,
      render: (values) => values?.companyName || '_',
    },
    {
      key: 'agencyName',
      title: 'Agency Name',
      dataIndex: 'payrollRef',
      width: 350,
      render: (values) => values.agencyName || '_',
    },
    {
      key: 'timesheetValue',
      title: 'Timesheet Value',
      dataIndex: 'timesheetValue',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'hours',
      title: 'Hours',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => props?.payeeUmbrellaValue?.totalHours || '',
    },
    {
      key: 'margin',
      title: 'Margin',
      dataIndex: 'margin',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => formatMoney(value || 0, currencySymbol),
    },
    {
      key: 'payrollRef',
      title: 'Apprentice Levy',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.appLevy || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'salary',
      title: 'Salary',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.salary || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'expenseClaimed',
      title: 'Expenses Claimed',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.expenseClaimed || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'expensesPaid',
      title: 'Expenses Paid',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.expensesPaid || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'bonus',
      title: 'Bonus',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.bonus || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'otherPayment',
      title: 'Other Payment',
      dataIndex: 'otherPayment',
      align: 'right',
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'otherDeduction',
      title: 'Other Deduction',
      dataIndex: 'otherDeduction',
      align: 'right',
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'totalPayment',
      title: 'Total Payment',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.totalPayment || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'tax',
      title: 'Tax Deducted',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.tax || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'employeeNI',
      title: 'Employee NI',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.employeeNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'employersNI',
      title: 'Employer NI',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.employersNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'totalDeduction',
      title: 'Total Deduction',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.totalDeduction || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'netPay',
      title: 'Net Pay',
      dataIndex: 'netPay',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'studentLoan',
      title: 'Student Loan',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.studentLoan || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'postgraduateLoan',
      title: 'PG Student Loan',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props?.postgraduateLoan || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'attachment',
      title: 'Attachment',
      dataIndex: 'attachment',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'slickPay',
      title: 'Sick Pay (SSP)',
      dataIndex: 'slickPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'maternityPay',
      title: 'Maternity Pay (SMP)',
      dataIndex: 'maternityPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'adoptionPay',
      title: 'Adoption Pay (SAP)',
      dataIndex: 'adoptionPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'paternityPay',
      title: 'Paternity Pay (SPP)',
      dataIndex: 'paternityPay',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'additionalSPP',
      title: 'Additional SPP',
      dataIndex: 'additionalSPP',
      align: 'right',
      render: (props) => <span>{formatMoney(props, currencySymbol)}</span>,
    },
    {
      key: 'payrollRef',
      title: 'Payment Date',
      dataIndex: 'payrollRef',
      align: 'right',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      render: (values) =>
        values?.paymentDate
          ? moment(values?.paymentDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'paid',
      title: 'Paid',
      align: 'right',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>{formatMoney(props?.paid || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'outstanding',
      title: 'Outstanding',
      align: 'right',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>{formatMoney(props?.outstanding || 0, currencySymbol)}</span>
      ),
    },
    {
      key: 'fpsAmount',
      title: 'FPS Amount',
      dataIndex: 'fpsAmount',
      align: 'right',
      render: (props) => <span>{formatMoney(props || 0, currencySymbol)}</span>,
    },
    {
      key: 'grossForTax',
      title: 'Gross For Tax',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.grossForTaxAndNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'grossForNI',
      title: 'Gross For NI',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.grossForTaxAndNI || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
    {
      key: 'grossForTaxYTD',
      title: 'Gross For Tax YTD',
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>
          {formatMoney(props?.payeeUmbrellaValue?.taxYTD || 0, currencySymbol)}
        </span>
      ),
    },
    {
      key: 'grossPayYTD',
      title: 'Gross for NI YTD',
      dataIndex: 'payrollRef',
      align: 'right',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.grossPayYTD || 0,
            currencySymbol,
          )}
        </span>
      ),
    },
  ];
}

export function getPayslipColumns(
  params: GetPayslipColumnsParams,
  isBureau: boolean,
): ColumnsType<any> {
  const {
    payrollCountry,
    permissions,
    paymentModel,
    onDownload,
    onViewDetail,
    messages,
    downloadLoadingId,
  } = params;
  const permissionHP = includes(permissions, PERMISSION_HOLIDAY_PAY);
  const currencySymbol = showCurrencyByCountry(payrollCountry);

  if (isBureau) {
    if (payrollCountry === IRELAND) {
      return getIrelandColumns({
        permissionHP,
        currencySymbol,
        onDownload,
        onViewDetail,
        messages,
        downloadLoadingId,
      });
    }

    return getDefaultColumns({
      permissionHP,
      currencySymbol,
      onDownload,
      onViewDetail,
      messages,
      downloadLoadingId,
    });
  }

  if (includes(['HYBRID', 'PAYE_HYBRID'], paymentModel)) {
    return getColumnsHybrid({
      payrollCountry,
      onDownload,
      onViewDetail,
      downloadLoadingId,
    });
  }

  if (includes(['UMBRELLA', 'PAYEE'], paymentModel)) {
    return getColumnsPayeeUmbrella({
      permissionHP,
      payrollCountry,
      onDownload,
      onViewDetail,
      downloadLoadingId,
    });
  }

  return getColumnsCis({
    payrollCountry,
    onDownload,
    onViewDetail,
    downloadLoadingId,
  });
}

export function withStatusColumn(
  columns: ColumnsType<any>,
  messages: TypedMessages,
): ColumnsType<any> {
  const statusCol: ColumnsType<any>[number] = {
    key: 'status',
    title: messages['common.status'] || 'Status',
    align: 'center',
    width: 150,
    render: (_, record) => {
      const { label, color } = getPayslipStatus(record);
      return <AppTagStatus color={color}>{label}</AppTagStatus>;
    },
  };

  // Insert after the first column (actions)
  const result = [...columns];
  result.splice(1, 0, statusCol);
  return result;
}
